import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MovieDetailModal from './MovieDetailModal';

import mastKalandar from '../assets/Released/mast-kalandar.jpeg';
import aa from '../assets/Released/aa.jpeg';
import mandoPlus from '../assets/Released/mando-plus.jpeg';
import kuchikuKuchiku from '../assets/Released/kuchiku-kuchiku.jpeg';
import production01 from '../assets/UpComing/production-01.jpeg';

const movies = [
  {
    id: 1,
    title: 'Mast Kalandar',
    genre: 'Drama',
    year: '2024',
    rating: '8.4',
    poster: mastKalandar,
    desc: 'Audio Super Hit',
    trailerUrl: null,
  },
  {
    id: 2,
    title: 'Aa!',
    genre: 'Horror',
    year: '2025',
    rating: '7.9',
    poster: aa,
    desc: 'A true Epic Horror story. 12 PM to 12 am.',
    trailerUrl: 'https://www.youtube.com/watch?v=nNW9eCwS2Dk',
  },
  {
    id: 3,
    title: 'Mando Plus',
    genre: 'Comedy Drama',
    year: '2026',
    rating: '8.1',
    poster: mandoPlus,
    desc: 'Full video out now.',
    trailerUrl: 'https://www.youtube.com/watch?v=3-SyM_cSbok',
  },
  {
    id: 4,
    title: 'Kuchiku Kuchiku',
    genre: 'Thriller',
    year: '2026',
    rating: '8.6',
    poster: kuchikuKuchiku,
    desc: 'Releasing tomorrow evening.',
    trailerUrl: 'https://www.youtube.com/watch?v=eAu0ORAJdcw',
  },
];

const upcoming = [
  {
    id: 5,
    title: 'Production #01',
    genre: 'Mystery',
    year: '202*',
    rating: 'Coming Soon',
    poster: production01,
    desc: 'The Details Will Be Revealed Very Soon',
    trailerUrl: null,
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function MovieCard({ movie, index, onOpen }) {
  const [hovered, setHovered] = useState(false);

  const handleTrailerClick = (e) => {
    e.stopPropagation();
    if (movie.trailerUrl) {
      window.open(movie.trailerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDetailsClick = (e) => {
    e.stopPropagation();
    onOpen(movie);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="poster-card h-[340px] sm:h-[380px] md:h-[460px] cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img src={movie.poster} alt={movie.title} />

      {/* Overlay */}
      <div className="poster-overlay">
        {/* Genre badge */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="bg-brand-orange/90 text-white text-[9px] sm:text-[10px] font-cinzel tracking-widest uppercase px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-sm">
            {movie.genre}
          </span>
          <span className="text-white/50 text-[10px] sm:text-xs font-inter">{movie.year}</span>
        </div>

        <h3 className="font-cinzel font-bold text-white text-sm sm:text-base md:text-lg leading-tight mb-1">
          {movie.title}
        </h3>

        {/* Watch Trailer link always visible under title */}
        {movie.trailerUrl && (
          <a
            href={movie.trailerUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-inter text-brand-orange/80 hover:text-brand-orange transition-colors duration-200 mb-2"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            Watch Trailer
          </a>
        )}

        <AnimatePresence>
          {hovered && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-white/60 text-[10px] sm:text-xs font-inter leading-relaxed mt-1 mb-3">
                {movie.desc}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-brand-gold font-cinzel text-xs font-semibold">
                  {movie.rating !== 'Coming Soon' ? `IMDb ${movie.rating}` : movie.rating}
                </span>
                <button
                  onClick={handleDetailsClick}
                  className="text-[9px] sm:text-[10px] font-cinzel tracking-widest uppercase text-brand-orange border border-brand-orange/50 px-2.5 sm:px-3 py-1.5 hover:bg-brand-orange hover:text-white transition-all duration-300 rounded-sm"
                >
                  Details
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile: always show details button (no hover on touch) */}
        <div className="sm:hidden flex items-center justify-between mt-2">
          <span className="text-brand-gold font-cinzel text-[10px] font-semibold">
            {movie.rating !== 'Coming Soon' ? `IMDb ${movie.rating}` : movie.rating}
          </span>
          <button
            onClick={handleDetailsClick}
            className="text-[9px] font-cinzel tracking-widest uppercase text-brand-orange border border-brand-orange/50 px-2.5 py-1 hover:bg-brand-orange hover:text-white transition-all duration-300 rounded-sm"
          >
            Details
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Productions() {
  const [tab, setTab] = useState('movies');
  const [selectedMovie, setSelectedMovie] = useState(null);
  const titleRef = useReveal();

  const displayList = tab === 'movies' ? movies : upcoming;

  return (
    <section id="productions" className="relative py-24 bg-brand-dark overflow-hidden">
      {/* Background decorative */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-orange/5 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-gold/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div ref={titleRef} className="reveal text-center mb-16">
          <span className="section-label block mb-4">Our Work</span>
          <h2 className="font-cinzel font-black text-3xl sm:text-4xl md:text-5xl text-white mb-5">
            Magic Moon{' '}
            <span className="text-gold-gradient">Productions</span>
          </h2>
          <div className="gold-divider mb-6" />
          <p className="font-inter text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
            From grand epics to intimate dramas, every production carries the
            signature of cinematic excellence that defines Magic Moon Studios.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="glass-card inline-flex rounded-sm p-1 gap-1">
            {[
              { key: 'movies', label: 'Our Movies' },
              { key: 'upcoming', label: 'Upcoming' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`relative px-4 sm:px-6 py-2.5 font-cinzel text-xs tracking-widest uppercase rounded-sm transition-all duration-400 ${
                  tab === t.key
                    ? 'text-brand-black'
                    : 'text-white/60 hover:text-brand-gold'
                }`}
              >
                {tab === t.key && (
                  <motion.div
                    layoutId="tabBg"
                    className="absolute inset-0 rounded-sm"
                    style={{
                      background: 'linear-gradient(135deg,#f37021,#D4AF37)',
                    }}
                  />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Movie grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6"
          >
            {displayList.map((movie, i) => (
              <MovieCard key={movie.id} movie={movie} index={i} onOpen={setSelectedMovie} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Bottom CTA */}
        <div className="text-center mt-14">
          <button className="border border-brand-gold/40 text-brand-gold font-cinzel text-xs tracking-widest uppercase px-8 sm:px-10 py-4 rounded-sm hover:bg-brand-gold/10 hover:border-brand-gold transition-all duration-300">
            View Full Portfolio
          </button>
        </div>
      </div>

      {/* Movie Detail Modal */}
      <AnimatePresence>
        {selectedMovie && (
          <MovieDetailModal
            movie={selectedMovie}
            onClose={() => setSelectedMovie(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
